import { useCallback } from 'react';

import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import type { WorkoutCreate } from 'types';
import type { AssignmentTarget } from 'utils/workout.helpers';

export interface AssignTracking {
  trackDateChanged: (fromDate: string, toDate: string) => void;
  trackConfirmed: (payload: WorkoutCreate) => void;
  trackCancelled: () => void;
}

/** The assignment events describe the athlete flow, so a class target stays silent. */
export default function useAssignTracking(target: AssignmentTarget | undefined): AssignTracking {
  const { trackAssignDateChanged, trackAssignWorkoutConfirmed, trackAssignWorkoutCancelled } =
    useStaffTracking();
  const isAthlete = target?.kind === 'athlete';

  const trackDateChanged = useCallback(
    (fromDate: string, toDate: string) => {
      if (isAthlete) trackAssignDateChanged({ from_date: fromDate, to_date: toDate });
    },
    [isAthlete, trackAssignDateChanged],
  );

  const trackConfirmed = useCallback(
    (payload: WorkoutCreate) => {
      if (!isAthlete) return;

      trackAssignWorkoutConfirmed({
        template_id: payload.workoutTemplate,
        date: payload.date,
        visible_to_athlete: payload.isVisibleToAthletes,
      });
    },
    [isAthlete, trackAssignWorkoutConfirmed],
  );

  const trackCancelled = useCallback(() => {
    if (isAthlete) trackAssignWorkoutCancelled();
  }, [isAthlete, trackAssignWorkoutCancelled]);

  return { trackDateChanged, trackConfirmed, trackCancelled };
}
